import {Component, OnDestroy, OnInit} from '@angular/core';
import {animate, sequence, style, transition, trigger} from '@angular/animations';
import {Location} from "@angular/common";
import { FormControl, FormGroup } from '@angular/forms';

import {debounceTime, filter, Observable, Subscription, switchMap, tap} from 'rxjs';
import { get } from 'lodash';
import { endOfDay, format, startOfDay } from 'date-fns';

import {EventTypeEnum} from '@commons/enums/event-type.enum';
import {EventDrawerService} from '@commons/services/map/drawer/event/event-drawer.service';
import {PanelNavigationService} from '@commons/services/navigation/panel-navigation.service';
import {EventService} from '@commons/services/event/event.service';
import {PermissionService} from "@shared/services/permission.service";
import { IEventResponse } from '@commons/models/remote.request/EventListFilterRequest';
import { BusinessEvent } from '@commons/models/domain/BusinessEvent';
import { LanguageService } from '@shared/services/language.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'tk-event-list-lite',
    templateUrl: './event-list-lite.component.html',
    styleUrls: ['./event-list-lite.component.scss'],
    animations: [
        trigger('openClose', [
            transition(":enter", [style({ transform: 'translateX(-264px)' }), animate('0.1s')]),
            transition(":leave", [sequence([style({ left: '56px', 'z-index': 0 }), animate('0.1s', style({ left: '-264px' }))])]),
        ])
    ]
})
export class EventListLiteComponent implements OnDestroy, OnInit {

    /**
     * Indicates active tab of specific event type
     */
    public eventType: EventTypeEnum = EventTypeEnum.Alarm;

    /**
     * Subscription to navigation changes
     */
    private navSub: Subscription;

    /**
     * Subscription to stream of count of unread events
     */
    private unreadEventCountSub?: Subscription;

    /**
     * Flag indicating whether event list is visible
     */
    public isVisible = false;

    /**
     * Event type enum used in template
     */
    public EventType = EventTypeEnum;

    /**
     * Unread types of events
     */
    public unreadTypes: EventTypeEnum[] = [];

    /**
     * Flag indicating whether this is first filter request
     */
    private firstFilterRequest = true;

    /**
     * czy użytkownik ma dostęp do zakładki alarmy
     */
    public isAlarmsTabEnabled = false;

    /**
     * czy użytkownik ma dostęp do zakładki paliwo
     */
    public isFuelTabEnabled = false;

    /**
    * czy użytkownik ma dostęp do zakładki obszary
    */
    public isAreasTabEnabled = false;

    /* ----- */
    public isLoadingData: boolean;
    public isVisibleDatepicker: boolean;
    public selectedDatesInCalendar: Date[];
    public formFilters: FormGroup = new FormGroup({
        Phrase: new FormControl(''),
        EventActionTypeAlarm: new FormControl(),
        EventActionTypeFuel: new FormControl(),
        EventActionTypeArea: new FormControl(),
        Dates: new FormControl([]),
    });
    public eventsList: BusinessEvent[] = [];

    public readonly ComponentSubsc: Subscription = new Subscription();
    public GetEventsSubsc: Subscription;

    /**
     * Event list lite's constructor
     * @param $ws web socket service
     * @param $nav navigation service
     * @param $event event service
     * @param $areaDrawer area drawer service
     */
    constructor(
        private $nav: PanelNavigationService,
        private $event: EventService,
        private $eventDrawer: EventDrawerService,
        private location: Location,
        private permissionService: PermissionService,
        private languageService: LanguageService,
        private translate: TranslateService,
    ) {
        this.isAlarmsTabEnabled = this.isAlarmsPermitted();
        this.isFuelTabEnabled = this.isFuelPermitted();
        this.isAreasTabEnabled = this.isAreasPermitted();

        // Subscribe to stream of url changes
        this.navSub = this.$nav.getVisibilitySubject()
            .pipe(
                filter(item => (!item.subsites || item.subsites.indexOf('event-lite') !== -1)),
                debounceTime(50),
            )
            .subscribe(change => {
                this.isVisible = change.show;
                if (this.isVisible) {
                    this.setCurrentTabNameInUrl(EventTypeEnum[this.eventType].toLowerCase());
                    this.getEvents().subscribe();
                }
                else
                    this.$eventDrawer.removeAll();
            });

        // // If new event is pushed send filter request to get new unread events
        // this.unreadEventCountSub = this.$ws.OnEventNotification()
        //     .startWith()
        //     .switchMap(event => this.$event.findEvent(new EventListFilterRequest()))
        //     .map(events => {
        //         return events.reduce((tally, event) => {
        //             if (event.EventStatus === EventStatusEnum.Unread) {
        //                 tally.add(event.EventType);
        //             }
        //             return tally;
        //         }, new Set());
        //     })
        //     .subscribe(unreadTypes => {
        //         this.unreadTypes = Array.from(unreadTypes);
        //         this.markTabAsRead(this.eventType);
        //     });
    }

    ngOnInit(): void {
        this.onChangeAlarmFiltersData();
    }
    /**
     * Ng on destroy lifecycle hook
     */
    public ngOnDestroy(): void {
        this.ComponentSubsc.unsubscribe();
        this.GetEventsSubsc?.unsubscribe();
        if (this.navSub) {
            this.navSub.unsubscribe();
        }
        if (this.unreadEventCountSub) {
            this.unreadEventCountSub.unsubscribe();
        }
    }


    /**
     * Returns style class for tab with given event type
     * @param eventType event type
     */
    public getStyleClassForTab(eventType: EventTypeEnum): any {

        return {
            'event-list__tab--active': this.eventType === eventType,
            'event-list__tab--unread': this.unreadTypes.includes(eventType)
        };
    }


    /**
     * Switches to a tab with given event type
     * @param eventType event type
     */
    public goToTab(eventType: EventTypeEnum): any {
        this.isVisibleDatepicker = false;
        this.markTabAsRead(eventType);
        this.setCurrentTabNameInUrl(EventTypeEnum[eventType].toLowerCase(), EventTypeEnum[this.eventType].toLowerCase());
        this.eventType = eventType;
        this.getEventsAdSubsc();
    }

    private getEventsAdSubsc(): void {
        this.GetEventsSubsc?.unsubscribe();
        this.GetEventsSubsc = this.getEvents().subscribe();
    }

    private setCurrentTabNameInUrl(newTabName: string, oldTabName?: string) {
      const path = this.location.path();
      if (path.indexOf('&tab=') > -1)
        this.location.go(this.location.path().replace(`&tab=${oldTabName}`, `&tab=${newTabName}`));
      else
        this.location.go(`${this.location.path()}&tab=${newTabName}`);
    }

    /**
     * Marks given tab as read
     * @param eventType event type
     */
    private markTabAsRead(eventType: EventTypeEnum): void {

        const index = this.unreadTypes.indexOf(eventType);
        if (index > -1) {
            this.unreadTypes.splice(index, 1);
        }
    }

    /**
    * sprawdzenie uprawnień do zakładki alarmy
    */
    private isAlarmsPermitted(): boolean {
        return this.permissionService.hasAlarms_RAZ_PO();
    }

    /**
    * sprawdzenie uprawnień do zakładki paliwo
    */
    private isFuelPermitted(): boolean {
        return this.permissionService.hasStdReports_FUELALL_POL();
    }

    /**
    * sprawdzenie uprawnień do zakładki obszary
    */
    private isAreasPermitted(): boolean {
        return this.permissionService.hasStdReports_ROK_SP();
    }



    public onSelectDaysInCalendar(data: Date[]): void {
        this.selectedDatesInCalendar = [...data];
        this.formFilters.get('Dates')?.setValue(data);
    }

    private onChangeAlarmFiltersData(): void {
        this.GetEventsSubsc?.unsubscribe();

        const sub = this.formFilters.valueChanges
        .pipe(
            tap(() => this.isLoadingData = true),
            debounceTime(200),
            switchMap(() => this.getEvents()),
        )
        .subscribe();
        this.ComponentSubsc.add(sub);
    }

    private getEvents(): Observable<IEventResponse> {
        this.isLoadingData = true;
        return this.$event.findEvent(this.alarmFiltersRequestBody).pipe(
            tap(
                (data) => {
                    this.eventsList = this.transformData(this.sortEventsByDate(data.events));
                    this.isLoadingData = false;
                }
            ),
        );
    }

    private sortEventsByDate(data: BusinessEvent[]): BusinessEvent[] {
        return (data || []).sort(
            (a: BusinessEvent, b: BusinessEvent) => {
                const _a = new Date(a.TS).valueOf();
                const _b = new Date(b.TS).valueOf();

                if (_a < _b) {
                    return 1;
                }
                if (_a > _b) {
                    return -1;
                }
                return 0;
            }
        );
    }

    private get alarmFiltersRequestBody(): any {
        const { value: form } = this.formFilters;

        const getEventTypeParamFormName = (): string => {
            if (this.eventType === EventTypeEnum.Alarm) {
                return 'EventActionTypeAlarm';
            }
            if (this.eventType === EventTypeEnum.Fuel) {
                return 'EventActionTypeFuel';
            }
            if (this.eventType === EventTypeEnum.Area) {
                return 'EventActionTypeArea';
            }
            return '';
        };

        return {
            DateFrom: startOfDay(get(form, 'Dates[0]', new Date())),
            DateTo: endOfDay(get(form, 'Dates[1]', new Date())),
            EventActionType: get(form, `${getEventTypeParamFormName()}.Id`, 0),
            Phrase: form.Phrase,
            EventType: this.eventType,
        };
    }

    private transformData(data: BusinessEvent[]): BusinessEvent[] {
        const flatData: BusinessEvent[] = [];
        let currentGroup = '';
        const today = new Date();
        const yesterday = new Date();
        yesterday.setDate(today.getDate() - 1);

        const getDateGroupTitle = (date: Date) => {
          if (date.toDateString() === today.toDateString()) {
            return this.translate.instant('date-time.today');
          } else if (date.toDateString() === yesterday.toDateString()) {
            return this.translate.instant('date-time.yesterday');
          } else {
            return format(date, 'EEEE, d MMMM yyyy', { locale: this.languageService.localeVariableDateFns });
          }
        };

        data.forEach(item => {
            const itemDate = new Date(item.TS);
            const groupTitle = getDateGroupTitle(itemDate);

            if (groupTitle !== currentGroup) {
                flatData.push({ isGroup: true, title: groupTitle } as any);
                currentGroup = groupTitle;
            }
            flatData.push(item);
        });
        return flatData;
    }

}
